import { sessionStore } from '@/shared/auth/session-store';

import { apiUrl } from './api-url';
import type { TokenPair } from './types';

const REFRESH_LOCK_NAME = 'fandsdrop-partner:token-refresh';

/** 인증 API(/api/auth/*)의 401 은 토큰 만료가 아니라 로그인 실패라 갱신하지 않는다 */
function isAuthEndpoint(url: string): boolean {
  return new URL(url, 'http://localhost').pathname.startsWith('/api/auth/');
}

/** 여러 탭의 동시 갱신을 한 줄로 세운다 — Web Locks 가 없는 환경은 탭 안에서만 한 번 */
async function withRefreshLock<T>(task: () => Promise<T>): Promise<T> {
  if (typeof navigator !== 'undefined' && 'locks' in navigator) {
    return navigator.locks.request(REFRESH_LOCK_NAME, task);
  }
  return task();
}

/** 이 계정으로는 파트너 페이지를 쓸 수 없다는 서버 응답 — 다시 시도해도 같으므로 세션을 끝낸다 */
const ACCOUNT_ERROR_CODES = new Set(['AUTH_NOT_CREATOR', 'AUTH_ACCOUNT_DEACTIVATED']);

/** 오류 응답 본문의 코드 — 본문은 복제해서 읽어 호출한 쪽이 다시 읽을 수 있게 둔다 */
async function errorCode(response: Response): Promise<string | null> {
  try {
    const body = (await response.clone().json()) as { data?: { code?: unknown } };
    return typeof body.data?.code === 'string' ? body.data.code : null;
  } catch {
    return null;
  }
}

let inFlight: Promise<string | null> | null = null;

/**
 * 만료된 access token 을 갱신한다 — 성공하면 새 access token, 세션을 이어갈 수 없으면 null.
 *
 * - 같은 탭의 동시 요청은 inFlight 하나를 기다린다 (refresh token 은 1회용이라 두 번 쓰면 둘째가 실패한다).
 * - 다른 탭과는 Web Lock 으로 줄을 선다. 락을 얻으면 저장소를 다시 읽고(storage 이벤트를 기다리지 않는다),
 *   access token 이 실패한 것과 다르면 다른 탭이 이미 갱신한 것이므로 그 토큰을 쓴다.
 * - 서버가 refresh token 을 거절해도, 그 사이 다른 탭이 갱신해 저장소의 refresh token 이 바뀌었으면 그걸 쓴다
 *   (Web Locks 가 없는 환경의 경합 대비). 정말 끝났을 때만 세션을 끝낸다.
 * - 429·5xx·네트워크 오류는 세션을 유지한다.
 */
export function refreshAccessToken(failedAccessToken: string): Promise<string | null> {
  inFlight ??= withRefreshLock(async () => {
    const current = sessionStore.reload();
    if (!current) return null;
    if (current.accessToken !== failedAccessToken) return current.accessToken;

    let response: Response;
    try {
      response = await fetch(apiUrl('/api/auth/refresh'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken: current.refreshToken }),
      });
    } catch {
      return null;
    }

    if (response.status === 401 || response.status === 403) {
      const latest = sessionStore.reload();
      if (latest && latest.refreshToken !== current.refreshToken) return latest.accessToken;
      const code = await errorCode(response);
      sessionStore.end(code && ACCOUNT_ERROR_CODES.has(code) ? 'account' : 'expired');
      return null;
    }
    if (!response.ok) return null;

    const tokens = (await response.json()) as TokenPair;
    sessionStore.updateTokens(tokens);
    return tokens.accessToken;
  }).finally(() => {
    inFlight = null;
  });
  return inFlight;
}

/**
 * openapi-fetch 에 넘기는 fetch — access token 을 붙이고, 401 이면 한 번 갱신한 뒤 같은 요청을 다시 보낸다.
 * 요청 본문은 한 번만 읽을 수 있어서 보내기 전에 복제해 둔다.
 * 크리에이터가 아니거나 비활성화된 계정(403)이면 세션을 끝내 로그인 화면으로 보낸다.
 */
export async function authFetch(request: Request): Promise<Response> {
  const session = sessionStore.get();
  const retry = session ? request.clone() : null;
  if (session) request.headers.set('Authorization', `Bearer ${session.accessToken}`);

  let response = await fetch(request);
  const isAuthApi = isAuthEndpoint(request.url);

  if (response.status === 401 && session && retry && !isAuthApi) {
    const accessToken = await refreshAccessToken(session.accessToken);
    if (accessToken) {
      retry.headers.set('Authorization', `Bearer ${accessToken}`);
      response = await fetch(retry);
    }
  }

  if (response.status === 403 && session && !isAuthApi) {
    const code = await errorCode(response);
    if (code && ACCOUNT_ERROR_CODES.has(code)) sessionStore.end('account');
  }
  return response;
}
